import { Outlet, useRouter } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Box, Flex, Heading } from '@chakra-ui/react';
import { LuFileSpreadsheet } from 'react-icons/lu';
import { Avatar } from '@/shared/ui/chakra/avatar';
import { LogoMark } from '@/shared/ui/Logo';
import { ColorModeButton, useColorModeValue } from '@/shared/ui/chakra/color-mode';
import { MenuContent, MenuItem, MenuRoot, MenuTrigger } from '@/shared/ui/chakra/menu';
import { useAuthStore } from '@/shared/lib/auth/store';
import { logout } from '@/features/auth/api/logout';
import { useInstallPrompt } from '@/features/pwa-install/useInstallPrompt';
import { ResponsiveShell } from '@/shared/ui/shell/ResponsiveShell';
import type { ShellNavSection } from '@/shared/ui/shell/types';

/* Provider "client cabinet" layout — the same responsive shell as the admin AppShell, trimmed to
   the cabinet's two destinations (refuels + the report builder mirror; with two items the shell's
   mobile tab bar kicks in on its own). Routing keeps this shell provider-only (see router
   `clientRoute`). */

export function ClientShell() {
  const { t } = useTranslation();
  const reset = useAuthStore((s) => s.reset);
  const router = useRouter();
  const logoTone = useColorModeValue<'brand' | 'onDark'>('brand', 'onDark');
  const { canInstall, promptInstall } = useInstallPrompt();

  const handleSignOut = async () => {
    try {
      await logout();
    } finally {
      reset();
      await router.navigate({ to: '/login' });
    }
  };

  const sections: ShellNavSection[] = [
    {
      key: 'main',
      items: [
        {
          key: '/client',
          to: '/client',
          label: t('client_reports.title'),
          icon: LuFileSpreadsheet,
          exact: true,
        },
      ],
    },
  ];

  return (
    <ResponsiveShell
      brand={
        <Flex align="center" gap="2.5" px="2">
          <LogoMark size={26} tone={logoTone} />
          <Heading size="md" color="colorPalette.fg">
            {t('client_cabinet.brand')}
          </Heading>
        </Flex>
      }
      sidebarAriaLabel={t('client_cabinet.brand')}
      sections={sections}
      menuTabLabel={t('layout.menu')}
      headerContent={
        <>
          <ColorModeButton />
          <MenuRoot>
            <MenuTrigger asChild>
              <Box as="button" aria-label={t('common.account')} cursor="pointer" rounded="full">
                <Avatar size="sm" name={t('client_cabinet.brand')} />
              </Box>
            </MenuTrigger>
            <MenuContent>
              {canInstall && (
                <MenuItem value="install" onClick={() => void promptInstall()}>
                  {t('pwa.install')}
                </MenuItem>
              )}
              <MenuItem value="sign-out" onClick={handleSignOut}>
                {t('common.sign_out')}
              </MenuItem>
            </MenuContent>
          </MenuRoot>
        </>
      }
      mobileMenuLabel={t('layout.open_menu')}
      mobileMenuTitle={t('client_cabinet.brand')}
    >
      <Outlet />
    </ResponsiveShell>
  );
}
